import React, { useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Box, Trash2 } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';

interface RegionSelectorProps {
  map: mapboxgl.Map | null;
  onRegionSelect: (
    bounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    } | null,
  ) => void;
  selectedRegion: {
    north: number;
    south: number;
    east: number;
    west: number;
  } | null;
  onDrawingStateChange?: (isDrawing: boolean) => void;
}

const RegionSelector: React.FC<RegionSelectorProps> = ({
  map,
  onRegionSelect,
  selectedRegion,
  onDrawingStateChange,
}) => {
  const { theme } = useTheme();
  const [isDrawing, setIsDrawing] = useState(false);
  const drawingLayerId = 'region-selection-layer';
  const drawingSourceId = 'region-selection-source';

  useEffect(() => {
    if (!map) return;

    let mouseDown = false;
    let startCoords: { lng: number; lat: number } | null = null;

    const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
      if (!isDrawing) return;

      // Prevent other event handlers from firing
      e.preventDefault();
      e.originalEvent.stopPropagation();

      mouseDown = true;
      startCoords = e.lngLat;

      // Remove existing selection
      if (map.getSource(drawingSourceId)) {
        if (map.getLayer(`${drawingLayerId}-stroke`)) {
          map.removeLayer(`${drawingLayerId}-stroke`);
        }
        if (map.getLayer(drawingLayerId)) {
          map.removeLayer(drawingLayerId);
        }
        map.removeSource(drawingSourceId);
      }
    };

    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (!isDrawing || !mouseDown || !startCoords) return;

      // Prevent other event handlers from firing
      e.preventDefault();
      e.originalEvent.stopPropagation();

      const currentCoords = e.lngLat;

      // Create rectangle coordinates
      const coordinates = [
        [
          [startCoords.lng, startCoords.lat],
          [currentCoords.lng, startCoords.lat],
          [currentCoords.lng, currentCoords.lat],
          [startCoords.lng, currentCoords.lat],
          [startCoords.lng, startCoords.lat],
        ],
      ];

      const geojson = {
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates,
        },
        properties: {},
      };

      if (map.getSource(drawingSourceId)) {
        (map.getSource(drawingSourceId) as mapboxgl.GeoJSONSource).setData(geojson);
      } else {
        // Remove any existing region layers first
        if (map.getLayer(`${drawingLayerId}-stroke`)) {
          map.removeLayer(`${drawingLayerId}-stroke`);
        }
        if (map.getLayer(drawingLayerId)) {
          map.removeLayer(drawingLayerId);
        }

        map.addSource(drawingSourceId, {
          type: 'geojson',
          data: geojson,
        });

        // Add fill layer at the very top
        map.addLayer({
          id: drawingLayerId,
          type: 'fill',
          source: drawingSourceId,
          paint: {
            'fill-color': '#0066FF', // Bright blue for maximum visibility
            'fill-opacity': 0.4, // Semi-transparent but visible
          },
        });

        // Add stroke layer at the very top (above fill)
        map.addLayer({
          id: `${drawingLayerId}-stroke`,
          type: 'line',
          source: drawingSourceId,
          paint: {
            'line-color': '#0066FF', // Bright blue for maximum visibility
            'line-width': 5, // Very thick border for visibility
            'line-dasharray': [12, 6], // Large, prominent dashes
          },
        });
      }

      // ALWAYS move layers to top after any update
      if (map.getLayer(drawingLayerId)) {
        map.moveLayer(drawingLayerId); // Move to top
      }
      if (map.getLayer(`${drawingLayerId}-stroke`)) {
        map.moveLayer(`${drawingLayerId}-stroke`); // Move to very top
      }
    };

    const onMouseUp = (e: mapboxgl.MapMouseEvent) => {
      if (!isDrawing || !mouseDown || !startCoords) return;

      // Prevent other event handlers from firing
      e.preventDefault();
      e.originalEvent.stopPropagation();

      mouseDown = false;
      const endCoords = e.lngLat;

      // Only create bounds if there's a meaningful area
      const deltaLat = Math.abs(endCoords.lat - startCoords.lat);
      const deltaLng = Math.abs(endCoords.lng - startCoords.lng);

      if (deltaLat > 0.001 && deltaLng > 0.001) {
        // Calculate bounds
        const bounds = {
          north: Math.max(startCoords.lat, endCoords.lat),
          south: Math.min(startCoords.lat, endCoords.lat),
          east: Math.max(startCoords.lng, endCoords.lng),
          west: Math.min(startCoords.lng, endCoords.lng),
        };

        onRegionSelect(bounds);
      }

      setIsDrawing(false);
      onDrawingStateChange?.(false);
      startCoords = null;
    };

    // Disable station interactions when drawing
    const disableStationInteractions = () => {
      if (isDrawing) {
        map.getCanvas().style.cursor = 'crosshair';
        // Disable pointer events on stations layer
        if (map.getLayer('stations')) {
          map.setLayoutProperty('stations', 'visibility', 'visible');
        }
      }
    };

    // Re-enable station interactions when not drawing
    const enableStationInteractions = () => {
      if (!isDrawing) {
        map.getCanvas().style.cursor = '';
      }
    };

    disableStationInteractions();
    enableStationInteractions();

    // Use higher priority event listeners
    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', onMouseUp);

    return () => {
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', onMouseUp);
      if (map.getCanvas()) {
        map.getCanvas().style.cursor = '';
      }
    };
  }, [map, isDrawing, onRegionSelect]);

  // Update map when selectedRegion changes
  useEffect(() => {
    if (!map) return;

    if (selectedRegion) {
      const coordinates = [
        [
          [selectedRegion.west, selectedRegion.north],
          [selectedRegion.east, selectedRegion.north],
          [selectedRegion.east, selectedRegion.south],
          [selectedRegion.west, selectedRegion.south],
          [selectedRegion.west, selectedRegion.north],
        ],
      ];

      const geojson = {
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates,
        },
        properties: {},
      };

      if (map.getSource(drawingSourceId)) {
        (map.getSource(drawingSourceId) as mapboxgl.GeoJSONSource).setData(geojson);
      } else {
        // Remove any existing region layers first
        if (map.getLayer(`${drawingLayerId}-stroke`)) {
          map.removeLayer(`${drawingLayerId}-stroke`);
        }
        if (map.getLayer(drawingLayerId)) {
          map.removeLayer(drawingLayerId);
        }

        map.addSource(drawingSourceId, {
          type: 'geojson',
          data: geojson,
        });

        // Add fill layer at the very top
        map.addLayer({
          id: drawingLayerId,
          type: 'fill',
          source: drawingSourceId,
          paint: {
            'fill-color': '#0066FF', // Bright blue for maximum visibility
            'fill-opacity': 0.4, // Semi-transparent but visible
          },
        });

        // Add stroke layer at the very top (above fill)
        map.addLayer({
          id: `${drawingLayerId}-stroke`,
          type: 'line',
          source: drawingSourceId,
          paint: {
            'line-color': '#0066FF', // Bright blue for maximum visibility
            'line-width': 5, // Very thick border for visibility
            'line-dasharray': [12, 6], // Large, prominent dashes
          },
        });
      }

      // ALWAYS move layers to top after any update
      if (map.getLayer(drawingLayerId)) {
        map.moveLayer(drawingLayerId); // Move to top
      }
      if (map.getLayer(`${drawingLayerId}-stroke`)) {
        map.moveLayer(`${drawingLayerId}-stroke`); // Move to very top
      }
    } else {
      // Remove selection
      if (map.getSource(drawingSourceId)) {
        if (map.getLayer(`${drawingLayerId}-stroke`)) {
          map.removeLayer(`${drawingLayerId}-stroke`);
        }
        if (map.getLayer(drawingLayerId)) {
          map.removeLayer(drawingLayerId);
        }
        map.removeSource(drawingSourceId);
      }
    }
  }, [map, selectedRegion]);

  const toggleDrawing = () => {
    const newDrawingState = !isDrawing;
    setIsDrawing(newDrawingState);

    // Notify parent component about drawing state change
    onDrawingStateChange?.(newDrawingState);

    if (map) {
      if (newDrawingState) {
        map.getCanvas().style.cursor = 'crosshair';
      } else {
        map.getCanvas().style.cursor = '';
      }
    }
  };

  const clearSelection = () => {
    onRegionSelect(null);
    setIsDrawing(false);
    onDrawingStateChange?.(false);
    if (map) {
      map.getCanvas().style.cursor = '';
    }
  };

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <button
        onClick={toggleDrawing}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          backgroundColor: isDrawing
            ? '#0066FF' // Bright blue for drawing
            : theme === 'dark'
              ? '#374151'
              : '#F3F4F6',
          color: isDrawing ? '#FFFFFF' : theme === 'dark' ? '#F9FAFB' : '#1F2937',
          border: 'none',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          if (!isDrawing) {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#4B5563' : '#E5E7EB';
          }
        }}
        onMouseOut={(e) => {
          if (!isDrawing) {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#374151' : '#F3F4F6';
          }
        }}
      >
        <Box size={14} />
        {isDrawing ? 'Drawing...' : 'Select Region'}
      </button>

      {selectedRegion && (
        <button
          onClick={clearSelection}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: theme === 'dark' ? '#DC2626' : '#EF4444',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#B91C1C' : '#DC2626';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#DC2626' : '#EF4444';
          }}
        >
          <Trash2 size={14} />
          Clear
        </button>
      )}
    </div>
  );
};

export default RegionSelector;
